<template>
  <HttpContentView :endpoint="utilities.path('users/@', userId)" v-model:reload="reload">
    <template v-slot:content="{ data }">
      <div class="row g-4">
        <div class="col-12 col-xl-6 mb-4">
          <DetailsCard :title="translate('name')" icon="fas fa-id-card text-primary">
            <template v-slot:content>
              <div class="d-flex align-items-center justify-content-between">
                <div style="font-size: 1.2em">{{ data.name }}</div>
                <ButtonView type="normal" color="primary" class="ms-2" @click="editingName = true">
                  <i class="fas fa-edit"></i>
                  {{ translate("edit") }}
                </ButtonView>
              </div>
              <div class="text-center mt-4" v-if="userId === info.user.id">
                <ButtonView type="normal" color="warning" @click="logoutConfirmation = true">
                  <i class="fas fa-right-from-bracket"></i>
                  {{ translate("logout") }}
                </ButtonView>
                <ModalView v-model:active="logoutConfirmation" size="small">
                  <template v-slot:body>
                    <HttpFormView endpoint="authentication/logout" method="post" @success="eventBus.emit('logout')">
                      <template v-slot:form="{ submit, loading }">
                        <div class="d-flex g-3">
                          <ButtonView type="normal" color="warning" class="w-100" @click="submit">
                            {{ translate("logout") }}
                          </ButtonView>
                          <ButtonView type="normal" color="secondary" class="w-100" @click="logoutConfirmation = false">
                            {{ translate("cancel") }}
                          </ButtonView>
                          <SpinnerView :active="loading" color="warning"/>
                        </div>
                      </template>
                    </HttpFormView>
                  </template>
                </ModalView>
              </div>
            </template>
          </DetailsCard>
        </div>
        <div class="col-12 col-xl-6 mb-4">
          <DetailsCard :title="translate('email')" icon="fas fa-envelope text-info">
            <template v-slot:content>
              <div class="d-flex align-items-center g-4 justify-content-between mb-4">
                <div class="mb-0">{{ data.email }}</div>
                <ButtonView type="normal" color="primary" class="ms-2" @click="editingEmail = { step: 1 }">
                  <i class="fas fa-edit"></i>
                  {{ translate("edit") }}
                </ButtonView>
              </div>
              <HttpFormView :endpoint="utilities.path('users/@/consents/newsletter', userId)" method="post" :content="{ consent: data.newsletterConsent }" @success="reload = true">
                <template v-slot:form="{ content, errors, submit, loading }">
                  <div class="position-relative">
                    <CheckboxInputView class="text-start" :title="translate('newsletter.consent.text')" :errors="errors.consent" :value="content.consent" @update:value="content.consent = $event; submit()" color="primary" :disabled="loading"/>
                    <SpinnerView color="primary" :active="loading"/>
                  </div>
                </template>
              </HttpFormView>
            </template>
          </DetailsCard>
        </div>
        <div v-if="info.global?.languages" class="col-12 col-xl-6 mb-4">
          <DetailsCard :title="translate('language')" icon="fas fa-language text-info">
            <template v-slot:content>
              <HttpFormView :endpoint="utilities.path('users/@/language', userId)" method="post" :content="{ code: info.global.language ?? info.global.languages.find(item => item.code === localization.browserLanguage())?.code ?? 'en' }" @success="info.reload(); localization.reloadTranslations()">
                <template v-slot:form="{ content, submit, errors, loading }">
                  <SelectInputView :options="info.global.languages.map(item => ({ ...item, id: item.code }))" :value="content.code" @update:value="content.code = $event; submit()" :errors="errors.code" required :title="translate('language')" searchable :disabled="loading">
                    <template v-slot:option="{ option }">
                      {{ option.flag }} {{ option.name }}
                    </template>
                  </SelectInputView>
                  <SpinnerView color="primary" :active="loading"/>
                </template>
              </HttpFormView>
            </template>
          </DetailsCard>
        </div>
      </div>
      <ModalView v-model:active="editingName" size="small">
        <template v-slot:body>
          <HttpFormView :endpoint="utilities.path('users/@/name', userId)" method="post" :content="{ name: data.name }" @success="editingName = false; nameChanged()">
            <template v-slot:form="{ content, errors, submit, loading }">
              <InputView type="text" :title="translate('name')" :errors="errors.name" required v-model:value="content.name" :max="24"/>
              <div class="text-light small fw-bold mt-1 ms-3">a-z A-Z 0-9 _ -</div>
              <div class="d-flex align-items-center justify-content-between mt-4 g-3">
                <ButtonView type="normal" color="primary" class="w-100" @click="submit">
                  {{ translate("save") }}
                </ButtonView>
                <ButtonView type="normal" color="secondary" class="w-100" @click="editingName = false">
                  {{ translate("cancel") }}
                </ButtonView>
              </div>
              <SpinnerView :active="loading" color="primary"/>
            </template>
          </HttpFormView>
        </template>
      </ModalView>
      <ModalView :active="editingEmail !== undefined" @update:active="editingEmail = undefined" size="small">
        <template v-slot:body>
          <HttpFormView v-if="editingEmail.step === 1" :endpoint="utilities.path('users/@/email/code/send', userId)" method="post" @success="editingEmail.step = 2">
            <template v-slot:form="{ content, errors, submit, loading }">
              <InputView type="email" :title="translate('email')" :errors="errors.email" :value="content.email" @update:value="content.email = $event; editingEmail.email = content.email" :disabled="loading" required/>
              <div class="d-flex align-items-center justify-content-between mt-4 g-3">
                <ButtonView type="normal" color="primary" class="w-100" @click="submit">
                  {{ translate("continue") }}
                </ButtonView>
                <ButtonView type="normal" color="secondary" class="w-100" @click="editingEmail = undefined">
                  {{ translate("cancel") }}
                </ButtonView>
              </div>
              <SpinnerView :active="loading" color="primary"/>
            </template>
          </HttpFormView>
          <HttpFormView v-else-if="editingEmail.step === 2" :endpoint="utilities.path('users/@/email', userId)" method="post" :content="{ email: editingEmail.email }" @success="editingEmail = undefined; reload = true">
            <template v-slot:form="{ content, errors, submit, loading }">
              <div class="d-flex flex-column g-4">
                <InputView type="email" :title="translate('email')" :errors="errors.email" v-model:value="content.email" disabled required/>

                <InputView type="text" :title="translate('user.email.change.code.current')" :errors="errors.currentEmailCode" v-model:value="content.currentEmailCode" :max="6" required/>
                <InputView type="text" :title="translate('user.email.change.code.new')" :errors="errors.newEmailCode" v-model:value="content.newEmailCode" :max="6" required/>
                <div class="text-center" style="font-size: 0.9em">{{ translate("user.email.change.code.instructions") }}</div>

                <div class="d-flex align-items-center justify-content-between g-3">
                  <ButtonView type="normal" color="primary" class="w-100" @click="submit">
                    {{ translate("save") }}
                  </ButtonView>
                  <ButtonView type="normal" color="secondary" class="w-100" @click="editingEmail = undefined">
                    {{ translate("cancel") }}
                  </ButtonView>
                </div>
              </div>
              <SpinnerView :active="loading" color="primary"/>
            </template>
          </HttpFormView>
        </template>
      </ModalView>
    </template>
  </HttpContentView>
</template>

<script setup>
import {utilities, HttpContentView, CheckboxInputView, SelectInputView, ButtonView, ModalView, HttpFormView, SpinnerView, InputView, ModalCloseButtonView} from "@/niftybit";
import DetailsCard from "@/components/global/DetailsCard.vue";
import info from "@/info";
import {computed, ref} from "vue";
import eventBus from "@/eventBus";
import localization from "@/localization";

defineProps({
  userId: String
});

const reload = ref(false);
const editingName = ref(false);
const editingEmail = ref(undefined);
const logoutConfirmation = ref(false);

function nameChanged() {
  reload.value = true;
  info.reload();
}
</script>